import React, { useMemo } from 'react';
import Banner from '../../../../../components/Banner';
import IconAiArrow from '../../../../../components/IconWrapper/arrow-ai';
import usePlatform from '../../../../../hooks/usePlatform';
import { getBannerOperationExtraAttr } from '../../../../../scripts/utils';
import './index.scss';
import '../../../../../components/AIP/aip-banner-wrap.scss';

type NoteTip = { value?: string; label?: string; img?: string };

const bannerBg = '/images/aip/Planning/planning-banner-bg.svg';

export default function Planning() {
  const { isPc } = usePlatform() || {};

  const bannerConfig = useMemo(() => {
    // 1) 来自 resources/Planning/index.html hero .container
    const title = '业务应用-计划';
    const legend = '从需求到交付，一套计划驱动全链路高效运转';
    const note =
      '面对需求波动、库存压力与交付不确定性，企业常陷入“计划频繁调整、执行反复救火”的循环。\n我们帮助企业构建贯穿战略与执行的计划体系，将分散决策转变为统一协同，让每一次计划都更可执行、更可兑现。';

    const noteTips: NoteTip[] = [
      { value: '-70%', label: '计划编制周期' },
      { value: '+45%', label: '库存周转率' },
      { value: '+35%', label: '订单准交率' }
    ];

    const src = bannerBg;

    return {
      src,
      mobileSrc: bannerBg,
      title,
      subheading: '',
      legend,
      note,
      noteTips,
      forceShowNote: true,
      operation: [
        {
          text: '立即咨询',
          icon: <IconAiArrow />,
          extraAttr: getBannerOperationExtraAttr({
            jumpBO: { formBOId: 1, formBO: { id: 1 }, jumpContentType: 'FORM' }
          })
        }
      ]
    };
  }, [isPc]);

  // 需求：忽略 resources/Planning 的 logo.png，因此不渲染 topbar

  return (
    <div className="planning-page">
      <div className="aip-banner-wrap planning-banner">
        <Banner
          showAdvantage={false}
          covered
          useArrow={false}
          useDots={false}
          size="large"
          type={'BANNER_TYPE.CAROUSEL'}
          name={typeof bannerConfig.title === 'string' ? bannerConfig.title : undefined}
          height={isPc ? 700 : 600}
          config={bannerConfig as any}
        />
      </div>

      {/* 2) hero 之后的所有 section，直接放在 banner 之后展示 */}
      <main>
        {/* 计划一体化闭环 */}
        <section className="section">
          <div className="container">
            <h2 className="section-title">计划一体化闭环</h2>
            <p className="section-subtitle feature-subtitle">用一套计划贯穿始终</p>
            <div className="feature-split">
              <div className="feature-list">
                <article className="feature-item feature-item-static">
                  <div className="feature-head">
                    <span className="feature-title">SOP统一经营目标</span>
                    <span className="feature-index">#1</span>
                  </div>
                  <p className="feature-desc">统一销售、生产、采购、财务目标，形成全局一致的销售与运营计划，锚定经营方向。</p>
                </article>
                <article className="feature-item feature-item-static">
                  <div className="feature-head">
                    <span className="feature-title">MPS/MRP驱动业务执行</span>
                    <span className="feature-index">#2</span>
                  </div>
                  <p className="feature-desc">基于有限产能将经营目标拆解为主生产计划和物料计划，确保计划指导采购与生产。</p>
                </article>
                <article className="feature-item feature-item-static">
                  <div className="feature-head">
                    <span className="feature-title">APS 智能排产</span>
                    <span className="feature-index">#3</span>
                  </div>
                  <p className="feature-desc">设备、人力、物料全约束建模，自动生成车间最优作业序列，支持插单、故障快速模拟。</p>
                </article>
                <article className="feature-item feature-item-static">
                  <div className="feature-head">
                    <span className="feature-title">计划分层落地履约</span>
                    <span className="feature-index">#4</span>
                  </div>
                  <p className="feature-desc">
                    从战略到执行的逐级细化，减少偏差与失真。联动仓储与物流，生成精准配送计划，保障订单按期、按质、按量交付。
                  </p>
                </article>
              </div>
              <div className="feature-preview">
                <div className="feature-placeholder">
                  <img src="/images/aip/Planning/integrated-planning-loop.png" alt="计划一体化闭环配图" loading="lazy" />
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* 智能计划引擎 */}
        <section className="section">
          <div className="container">
            <h2 className="section-title">智能计划引擎</h2>
            <p className="section-subtitle feature-subtitle">让计划支撑业务决策</p>
            <div className="feature-split">
              <div className="feature-list">
                <article className="feature-item feature-item-static">
                  <div className="feature-head">
                    <span className="feature-title">智能资源匹配</span>
                    <span className="feature-index">#1</span>
                  </div>
                  <p className="feature-desc">嵌入求解器，综合订单优先级、设备利用率、换模成本，自动输出资源最优分配方案。</p>
                </article>
                <article className="feature-item feature-item-static">
                  <div className="feature-head">
                    <span className="feature-title">有限产能排程</span>
                    <span className="feature-index">#2</span>
                  </div>
                  <p className="feature-desc">打破传统 MRP 无限产能假设，按实际产能、工时、物料齐套性生成可执行计划。</p>
                </article>
                <article className="feature-item feature-item-static">
                  <div className="feature-head">
                    <span className="feature-title">假设模拟分析</span>
                    <span className="feature-index">#3</span>
                  </div>
                  <p className="feature-desc">
                    支持插单、设备故障、物料延迟等场景模拟，快速评估对全局计划的影响，帮助业务选择更优执行路径。
                  </p>
                </article>
                <article className="feature-item feature-item-static">
                  <div className="feature-head">
                    <span className="feature-title">人机协同决策机制</span>
                    <span className="feature-index">#4</span>
                  </div>
                  <p className="feature-desc">系统提供优化建议，同时支持人工调整与锁定，兼顾自动化效率与业务灵活性。</p>
                </article>
              </div>
              <div className="feature-preview">
                <div className="feature-placeholder">
                  <img src="/images/aip/Planning/intelligent-planning-engine.png" alt="智能计划引擎配图" loading="lazy" />
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* 计划控制塔 */}
        <section className="section">
          <div className="container">
            <h2 className="section-title">计划控制塔</h2>
            <p className="section-subtitle feature-subtitle">让计划执行看得见控得住</p>
            <div className="feature-split">
              <div className="feature-list">
                <article className="feature-item feature-item-static">
                  <div className="feature-head">
                    <span className="feature-title">计划执行进度实时可视化</span>
                    <span className="feature-index">#1</span>
                  </div>
                  <p className="feature-desc">对接执行系统，动态采集进度数据，计划看板全程直观可查、可追溯。</p>
                </article>
                <article className="feature-item feature-item-static">
                  <div className="feature-head">
                    <span className="feature-title">多维度异常自动预警</span>
                    <span className="feature-index">#2</span>
                  </div>
                  <p className="feature-desc">针对缺料、延期、产能冲突等关键风险自动提醒，降低人工监控成本。</p>
                </article>
                <article className="feature-item feature-item-static">
                  <div className="feature-head">
                    <span className="feature-title">需求与执行偏差监控</span>
                    <span className="feature-index">#3</span>
                  </div>
                  <p className="feature-desc">实时识别需求波动与计划偏差，提前调整生产与采购策略。</p>
                </article>
                <article className="feature-item feature-item-static">
                  <div className="feature-head">
                    <span className="feature-title">KPI 驱动持续优化</span>
                    <span className="feature-index">#4</span>
                  </div>
                  <p className="feature-desc">通过准交率、计划达成率、库存周转等指标，持续评估与优化计划效果。</p>
                </article>
              </div>
              <div className="feature-preview">
                <div className="feature-placeholder">
                  <img src="/images/aip/Planning/planning-control-tower.png" alt="计划控制塔配图" loading="lazy" />
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* 端到端协同闭环 */}
        <section className="section">
          <div className="container">
            <h2 className="section-title">端到端协同闭环</h2>
            <p className="section-subtitle feature-subtitle">一套计划、一套数据、一个时间轴</p>
            <div className="feature-split">
              <div className="feature-list">
                <article className="feature-item feature-item-static">
                  <div className="feature-head">
                    <span className="feature-title">跨部门协同工作台</span>
                    <span className="feature-index">#1</span>
                  </div>
                  <p className="feature-desc">销售、计划、生产、采购、财务在同一平台协作，减少信息传递与沟通成本。</p>
                </article>
                <article className="feature-item feature-item-static">
                  <div className="feature-head">
                    <span className="feature-title">任务一键分发</span>
                    <span className="feature-index">#2</span>
                  </div>
                  <p className="feature-desc">计划自动分解、责任到人、节点明确，编制 — 执行 — 监控 — 优化全程闭环。</p>
                </article>
                <article className="feature-item feature-item-static">
                  <div className="feature-head">
                    <span className="feature-title">统一业务语义</span>
                    <span className="feature-index">#3</span>
                  </div>
                  <p className="feature-desc">计划、订单、物料、产能数据统一管理，避免多版本数据带来的决策偏差。</p>
                </article>
                <article className="feature-item feature-item-static">
                  <div className="feature-head">
                    <span className="feature-title">柔性生产适配</span>
                    <span className="feature-index">#4</span>
                  </div>
                  <p className="feature-desc">支持多生产模式，兼容物料替代、JIT 拉动、多工厂协同，提升整体供应链响应效率。</p>
                </article>
              </div>
              <div className="feature-preview">
                <div className="feature-placeholder">
                  <img src="/images/aip/Planning/end-to-end-collaboration-loop.png" alt="端到端协同闭环配图" loading="lazy" />
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* 客户案例 - 暂时隐藏 */}
        {/* <section className="section" id="cases">
          <div className="container">
            <h2 className="section-title">客户案例</h2>
            <p className="section-subtitle feature-subtitle">以可验证的业务结果检验计划体系的价值</p>
            <div className="cases">
              <a className="case-link" href="https://terminus.io/case-detail/2009001" target="_blank" rel="noopener noreferrer">
                <article className="case-card">
                  <span className="case-badge">案例 01</span>
                  <div className="case-body">
                    <h3 className="case-title">三力士 中国橡胶V带制造企业龙头</h3>
                    <p className="case-desc">以“生产制造”为核心，助力企业供应链整体数字化升级。</p>
                    <div className="case-illustration">
                      <img className="case-illustration-img" src="/images/case-01.png" alt="案例01配图" loading="lazy" />
                    </div>
                  </div>
                </article>
              </a>

              <a className="case-link" href="https://terminus.io/case-detail/12080" target="_blank" rel="noopener noreferrer">
                <article className="case-card">
                  <span className="case-badge">案例 02</span>
                  <div className="case-body">
                    <h3 className="case-title">源氏木语 纯实木家居品类代表性企业</h3>
                    <p className="case-desc">全量业务数字化蜕变，打造家居行业数字化样板间。</p>
                    <div className="case-illustration">
                      <img className="case-illustration-img" src="/images/case-02.png" alt="案例02配图" loading="lazy" />
                    </div>
                  </div>
                </article>
              </a>
            </div>
          </div>
        </section> */}
      </main>
    </div>
  );
}

